import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, KeyRound, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { apiService, EtatSynchroUpSpot } from '@/services/api'

/* La carte « UP-SPOT » des paramètres (24/09/2026), réservée aux
 * administrateurs.
 *
 * UP-CRM est la porte d'entrée des comptes UP-SPOT, et tout compte doit avoir
 * sa fiche. La synchronisation, en temps réel, s'en charge seule dans les cas
 * simples. Cette carte montre ce qu'elle n'a pas pu trancher :
 *   - les comptes « à arbitrer » : plusieurs fiches portent leur adresse ;
 *   - les homonymes : une fiche créée automatiquement alors qu'une fiche au
 *     même nom existait sous une autre adresse — probablement la même personne ;
 *   - les échecs : un nom que la base d'UP-CRM ne sait pas enregistrer ;
 * et garde la trace des gestes des administrateurs sur les comptes.
 */

function dateEtHeure(valeur: string | null | undefined): string {
  if (!valeur) return '—'
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

const LIBELLE_ACTION: Record<string, string> = {
  creation: 'Création',
  role: 'Rôle',
  statut: 'Statut',
  service: 'Service',
  droits: 'Droits',
  invitation: 'Invitation',
  rattachement: 'Rattachement',
  identite: 'Nom et adresse',
  'fiche-supprimee': 'Fiche supprimée',
  'compte-supprime': 'Compte supprimé',
}

const lienFiche = (id: number, texte: string) => (
  <Link to={`/contacts?open=${id}`} className="font-semibold text-violet-700 hover:underline">{texte}</Link>
)

export default function UpSpotCard() {
  const { toast } = useToast()
  const [etat, setEtat] = useState<EtatSynchroUpSpot | null>(null)
  const [chargement, setChargement] = useState(true)
  const [synchro, setSynchro] = useState(false)
  const [choix, setChoix] = useState<Record<number, number>>({})
  const [envoi, setEnvoi] = useState<number | null>(null)
  const [voirCreees, setVoirCreees] = useState(false)

  const charger = useCallback(async () => {
    const r = await apiService.etatSynchroUpSpot()
    if (r.success && r.data) setEtat(r.data)
    setChargement(false)
  }, [])

  useEffect(() => { void charger() }, [charger])

  const synchroniser = async () => {
    setSynchro(true)
    const r = await apiService.synchroniserUpSpot()
    setSynchro(false)
    if (r.success && r.data) {
      setEtat(r.data)
      toast({ title: 'Synchronisation terminée.' })
    } else {
      toast({ title: 'La synchronisation a échoué', description: r.error, variant: 'destructive' })
    }
  }

  const rattacher = async (compteId: number, contactId: number) => {
    setEnvoi(compteId)
    const r = await apiService.rattacherCompteUpSpot(compteId, contactId)
    setEnvoi(null)
    if (r.success) {
      toast({ title: 'Compte rattaché à la fiche choisie.' })
      await charger()
    } else {
      toast({ title: 'Rattachement impossible', description: r.error, variant: 'destructive' })
    }
  }

  const rapport = etat?.rapport

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-violet-600" />
          UP-SPOT
        </CardTitle>
        <CardDescription>
          UP-CRM est la porte d'entrée des comptes UP-SPOT : chaque compte a sa fiche contact, et les comptes se
          gèrent depuis la fiche. La synchronisation se fait en temps réel ; voici ce qu'elle vous laisse à trancher.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {chargement ? (
          <div className="h-4 bg-slate-200 rounded animate-pulse w-60" />
        ) : !etat?.configure ? (
          <p className="text-sm text-slate-500">
            UP-SPOT n'est pas encore relié à UP-CRM : il manque les variables <code>UPSPOT_URL</code> et{' '}
            <code>UPSPOT_ACCESS_KEY</code> sur le serveur.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600 space-y-0.5">
                <p>
                  Dernier passage : <strong>{dateEtHeure(etat.termineeLe)}</strong>
                  {etat.declencheur && <span className="text-slate-400"> — {etat.declencheur}</span>}
                </p>
                {rapport && !rapport.erreur && (
                  <p>
                    {rapport.comptes} compte{rapport.comptes > 1 ? 's' : ''} dans UP-SPOT, dont {rapport.relies} relié
                    {rapport.relies > 1 ? 's' : ''} à une fiche.
                  </p>
                )}
                {rapport?.erreur && <p className="text-red-600">Le dernier passage a échoué : {rapport.erreur}</p>}
              </div>
              <Button variant="outline" size="sm" disabled={synchro} onClick={() => void synchroniser()}>
                {synchro ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Synchroniser maintenant
              </Button>
            </div>

            {rapport && rapport.aArbitrer.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-800">
                  À arbitrer ({rapport.aArbitrer.length}) — plusieurs fiches partagent l'adresse du compte
                </h4>
                {rapport.aArbitrer.map((a) => (
                  <div key={a.compte.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <p className="text-sm">
                      <strong>{a.compte.prenom} {a.compte.nom}</strong>{' '}
                      <span className="text-slate-500">{a.compte.email}</span>
                    </p>
                    <p className="text-xs text-amber-800">{a.motif}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm"
                        value={choix[a.compte.id] ?? ''}
                        onChange={(e) => setChoix({ ...choix, [a.compte.id]: Number(e.target.value) })}
                      >
                        <option value="">Choisir la fiche qui porte le compte…</option>
                        {a.candidats.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.prenom} {c.nom}{c.entreprise ? ` — ${c.entreprise}` : ' — sans entreprise'} (fiche n° {c.id})
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        disabled={!choix[a.compte.id] || envoi === a.compte.id}
                        onClick={() => void rattacher(a.compte.id, choix[a.compte.id])}
                      >
                        Rattacher
                      </Button>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {rapport && rapport.homonymes.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-800">
                  Homonymes à vérifier ({rapport.homonymes.length})
                </h4>
                <p className="text-xs text-slate-500">
                  Ces fiches ont été créées automatiquement, alors qu'une fiche au même nom existait déjà sous une autre
                  adresse. S'il s'agit de la même personne, rattachez le compte à la fiche d'origine, puis supprimez la
                  fiche créée en trop.
                </p>
                {rapport.homonymes.map((h) => (
                  <div key={h.contactId} className="rounded-lg border border-slate-200 p-3 space-y-1.5 text-sm">
                    <p>
                      Fiche créée : {lienFiche(h.contactId, `${h.compte.prenom} ${h.compte.nom}`)}{' '}
                      <span className="text-slate-500">{h.compte.email}</span>
                    </p>
                    {h.autres.map((o) => (
                      <div key={o.id} className="flex flex-wrap items-center gap-2 pl-3">
                        <span>
                          Même nom : {lienFiche(o.id, `${o.prenom} ${o.nom}`)}
                          <span className="text-slate-500">{o.entreprise ? ` — ${o.entreprise}` : ' — sans entreprise'}</span>
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={envoi === h.compte.id}
                          onClick={() => void rattacher(h.compte.id, o.id)}
                        >
                          Rattacher le compte à cette fiche
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </section>
            )}

            {rapport && rapport.echecs.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold text-slate-800">Comptes qui n'ont pas pu remonter ({rapport.echecs.length})</h4>
                {rapport.echecs.map((e) => (
                  <p key={e.compte.id} className="text-sm">
                    <strong>{e.compte.prenom} {e.compte.nom}</strong>{' '}
                    <span className="text-slate-500">{e.compte.email}</span>
                    <span className="block text-xs text-red-600">{e.erreur}</span>
                  </p>
                ))}
              </section>
            )}

            {rapport && rapport.fichesSupprimees.length > 0 && (
              <section className="space-y-1">
                <h4 className="text-sm font-semibold text-slate-800">
                  Comptes désactivés dont la fiche a été supprimée ({rapport.fichesSupprimees.length})
                </h4>
                <p className="text-xs text-slate-500">
                  Ils restent dans UP-SPOT pour garder l'historique des réservations, sans fiche dans UP-CRM.
                </p>
                <ul className="text-sm list-disc pl-5">
                  {rapport.fichesSupprimees.map((c) => (
                    <li key={c.id}>{c.prenom} {c.nom} <span className="text-slate-500">{c.email}</span></li>
                  ))}
                </ul>
              </section>
            )}

            {etat.fichesCreees.length > 0 && (
              <section className="space-y-1">
                <button
                  type="button"
                  className="text-sm font-semibold text-slate-800 hover:underline"
                  onClick={() => setVoirCreees(!voirCreees)}
                >
                  Fiches créées par la synchronisation ({etat.fichesCreees.length}) {voirCreees ? '▾' : '▸'}
                </button>
                {voirCreees && (
                  <ul className="text-sm list-disc pl-5">
                    {etat.fichesCreees.map((f) => (
                      <li key={f.contactId}>
                        {lienFiche(f.contactId, `${f.prenom} ${f.nom}`)} <span className="text-slate-500">{f.email}</span>
                        <span className="text-xs text-slate-400"> — {dateEtHeure(f.creeLe)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {etat.journal.length > 0 && (
              <section className="space-y-1">
                <h4 className="text-sm font-semibold text-slate-800">Derniers gestes sur les comptes</h4>
                <ul className="text-xs text-slate-600 space-y-0.5">
                  {etat.journal.map((j, i) => (
                    <li key={i}>
                      <span className="text-slate-400">{dateEtHeure(j.faitLe)}</span>{' '}
                      <strong>{LIBELLE_ACTION[j.action] ?? j.action}</strong> — {j.details}
                      {j.faitPar && <span className="text-slate-400"> (par {j.faitPar})</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
